import { useMemo, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { MfaRequiredError } from "../features/auth/errors";
import { getRoleDashboardPath, getRoleLabel } from "../features/auth/role";
import type { AuthSession } from "../features/auth/types";
import { useAuth } from "../features/auth/useAuth";

export function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, dashboardPath, login, verifyMfa } = useAuth();

  const [companyCode, setCompanyCode] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mfaChallengeToken, setMfaChallengeToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");

  const isDisabled = useMemo(
    () =>
      submitting ||
      companyCode.trim().length === 0 ||
      username.trim().length === 0 ||
      password.length === 0,
    [companyCode, password.length, submitting, username],
  );

  const isMfaDisabled = useMemo(
    () => submitting || mfaCode.trim().length !== 6,
    [mfaCode, submitting],
  );

  if (isAuthenticated) {
    return <Navigate to={dashboardPath} replace />;
  }

  function goToDashboard(session: AuthSession) {
    const roleLabel = getRoleLabel(session.appRole);
    const rolePath = getRoleDashboardPath(session.appRole);
    navigate(rolePath, {
      replace: true,
      state: { flash: `Signed in as ${roleLabel}` },
    });
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const session = await login({
        companyCode: companyCode.trim().toUpperCase(),
        username: username.trim(),
        password,
      });
      goToDashboard(session);
    } catch (requestError) {
      if (requestError instanceof MfaRequiredError) {
        setMfaChallengeToken(requestError.challengeToken);
        return;
      }
      setError(
        requestError instanceof Error ? requestError.message : "Unable to sign in",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmitMfa(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!mfaChallengeToken) return;
    setSubmitting(true);
    setError(null);

    try {
      const session = await verifyMfa(mfaChallengeToken, mfaCode.trim(), username.trim());
      goToDashboard(session);
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "Invalid verification code",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="app-shell auth-shell">
      <section className="hero fade-in">
        <p className="eyebrow">
          <span className="brand-wordmark">
            0todeploy <span className="brand-vms">VMS</span>
          </span>
        </p>
        <h1>Sign in to your dashboard</h1>
        <p className="subtitle">
          Role-based access routes users to Gate, Admin-HR, or Employee workflows.
        </p>
      </section>

      <section className="panel auth-card fade-in delay-1">
        {mfaChallengeToken ? (
          <>
            <header className="panel-header">
              <h2>Verification Code</h2>
              <span>Secure Session</span>
            </header>
            <form className="auth-form" onSubmit={onSubmitMfa}>
              <label htmlFor="mfa-code">Authenticator Code</label>
              <input
                id="mfa-code"
                name="mfa-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={mfaCode}
                onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit code"
                autoFocus
              />
              <button className="refresh-btn auth-submit" type="submit" disabled={isMfaDisabled}>
                {submitting ? "Verifying..." : "Verify"}
              </button>
            </form>
            {error ? <p className="error-banner auth-error">{error}</p> : null}
            <p className="auth-switch">
              <button
                className="link-btn"
                type="button"
                onClick={() => {
                  setMfaChallengeToken(null);
                  setMfaCode("");
                  setError(null);
                }}
              >
                Back to sign in
              </button>
            </p>
          </>
        ) : (
          <>
            <header className="panel-header">
              <h2>Login</h2>
              <span>Secure Session</span>
            </header>
            <form className="auth-form" onSubmit={onSubmit}>
              <label htmlFor="company-code">Company Code</label>
              <input
                id="company-code"
                name="company-code"
                autoComplete="off"
                value={companyCode}
                onChange={(event) => setCompanyCode(event.target.value)}
                placeholder="e.g. ACME"
                style={{ textTransform: "uppercase" }}
              />

              <label htmlFor="username">Username</label>
              <input
                id="username"
                name="username"
                autoComplete="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="Enter your username"
              />

              <label htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter your password"
              />

              <button className="refresh-btn auth-submit" type="submit" disabled={isDisabled}>
                {submitting ? "Signing in..." : "Sign in"}
              </button>
            </form>
            {error ? <p className="error-banner auth-error">{error}</p> : null}
            <p className="auth-switch">
              <Link to="/forgot-password">Forgot password?</Link>
            </p>
          </>
        )}
      </section>
    </main>
  );
}
